import {
  DetailRequirement,
  EngagementResourceDetailsQueryParams,
  GeneralResourceDetail,
  MpxResponseError,
  ProgramEngagementsAPI,
  ResourceDetailDeleteRequest,
} from '@cibo/core'
import { DetailsResponse } from '@cibo/core/src/api/types'
import {
  UseMutationOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { PROGRAMS_QUERY_KEY } from './queryKey'

export const useProgramEngagementDetails = <
  T extends GeneralResourceDetail = GeneralResourceDetail
>({
  detailRequirements,
  engagementId,
}: {
  detailRequirements?: DetailRequirement<T>[]
  engagementId?: string
}) =>
  useQuery({
    queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENT_DETAILS, engagementId],
    // @ts-ignore not enabled if there's no resource id
    queryFn: () => ProgramEngagementsAPI.getDetails<T>({ engagementId }),
    select: response => {
      return response.details?.filter(({ traitId, year }) =>
        detailRequirements
          ? !!detailRequirements.find(req => req.traitId === traitId && req.year === year)
          : true
      )
    },
    enabled: !!engagementId,
  })
export const useMultipleProgramEngagementsDetails = <
  T extends GeneralResourceDetail = GeneralResourceDetail
>({
  detailRequirements,
  resourceIds,
}: {
  detailRequirements?: DetailRequirement<T>[]
  resourceIds?: string[]
}) =>
  useQueries({
    queries:
      resourceIds?.map(engagementId => ({
        queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENT_DETAILS, engagementId],
        // @ts-ignore not enabled if there's no resource id
        queryFn: () => ProgramEngagementsAPI.getDetails<T>({ engagementId }),
        select: (response: DetailsResponse<T>) => {
          const detail = response.details?.filter(({ traitId, year }) =>
            detailRequirements
              ? !!detailRequirements.find(req => req.traitId === traitId && req.year === year)
              : true
          )
          return { resourceId: engagementId, detail }
        },
        enabled: !!engagementId,
      })) || [],
  })

export const useUpdateProgramEngagementDetails = <
  T extends GeneralResourceDetail = GeneralResourceDetail
>(
  props?: Pick<
    UseMutationOptions<
      unknown,
      AxiosError<MpxResponseError>,
      EngagementResourceDetailsQueryParams<T>,
      unknown
    >,
    'onSuccess' | 'onError'
  > & { engagementId: string; programId?: string }
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: EngagementResourceDetailsQueryParams<T>) => {
      return ProgramEngagementsAPI.updateDetails(params)
    },

    onError: props?.onError,
    onSuccess: (response, { engagementId }) => {
      queryClient.setQueryData(
        [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENT_DETAILS, engagementId],
        response
      )

      queryClient.invalidateQueries({
        queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, engagementId],
      })
      props?.programId &&
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS, { programId: [props?.programId] }],
        })
    },
  })
}

export const useRemoveProgramEngagementDetail = (
  props?: Pick<
    UseMutationOptions<unknown, AxiosError<MpxResponseError>, ResourceDetailDeleteRequest, unknown>,
    'onSuccess' | 'onError'
  >
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: ResourceDetailDeleteRequest) =>
      ProgramEngagementsAPI.removeDetail(request),

    onError: props?.onError,
    onSuccess: (event, updates) => {
      const engagementId = updates.engagementId

      //invalidate queries with this engagementId
      queryClient.invalidateQueries({
        queryKey: [
          PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENTS,
          // see useFields
          engagementId,
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [PROGRAMS_QUERY_KEY.PROGRAM_ENGAGEMENT_DETAILS, engagementId],
      })
    },
  })
}
